import { useEffect, useState, type ReactNode } from "react";
import DeadlineBanner from "@/components/feature/DeadlineBanner";
import useClassSchedules, { formatDateKorean, daysToLabel } from "@/hooks/useClassSchedules";

type HeroProps = {
  onApply: () => void;
  isManuallyClosed: boolean;
};

type HeroCardId = "travel" | "goal" | "media" | "daily";

type HeroCardConfig = {
  id: HeroCardId;
  colSpan: string;
  height: string;
  backTitle: string;
  backBody: string;
  backItems: string[];
  backAccent?: string;
};

const HERO_CARDS: HeroCardConfig[] = [
  {
    id: "travel",
    colSpan: "col-span-2 sm:col-span-4",
    height: "h-28 sm:h-56",
    backTitle: "여행 회화",
    backBody: "공항·역·식당·숙소에서 바로 쓰는 실전 표현을 익혀요.",
    backItems: ["「すみません、駅はどこですか？」", "「お会計お願いします」", "「チェックインお願いします」"],
    backAccent: "text-primary-600",
  },
  {
    id: "goal",
    colSpan: "col-span-2 sm:col-span-2",
    height: "h-28 sm:h-56",
    backTitle: "8주 후 목표",
    backBody: "외운 문장이 아니라, 상황에 맞게 스스로 말하는 힘을 만듭니다.",
    backItems: ["자기소개 · 기본 질문", "간단한 의견 · 부탁 표현", "여행·일상에서 바로 사용"],
    backAccent: "text-background-50",
  },
  {
    id: "media",
    colSpan: "col-span-1 sm:col-span-3",
    height: "h-24 sm:h-40",
    backTitle: "미디어 감상",
    backBody: "애니·드라마·음악을 자막 없이 더 깊게 즐기는 기초를 쌓아요.",
    backItems: ["「〜だよね」「〜かな」 구어체", "자주 나오는 감탄·반응 표현", "원문 그대로 따라 말하기"],
    backAccent: "text-accent-600",
  },
  {
    id: "daily",
    colSpan: "col-span-1 sm:col-span-3",
    height: "h-24 sm:h-40",
    backTitle: "일상 대화",
    backBody: "카페·SNS·온라인에서 자연스럽게 이어가는 짧은 대화를 연습해요.",
    backItems: ["「最近どう？」", "「また連絡してね」", "「今日はありがとう」"],
    backAccent: "text-primary-600",
  },
];

function HeroFlipCard({
  card,
  isFlipped,
  onToggle,
  front,
}: {
  card: HeroCardConfig;
  isFlipped: boolean;
  onToggle: () => void;
  front: ReactNode;
}) {
  const isGoal = card.id === "goal";
  const isCompact = card.id === "media" || card.id === "daily";

  return (
    <div className={`${card.colSpan} ${card.height} min-h-0`}>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={isFlipped}
        aria-label={`${card.backTitle} 카드 ${isFlipped ? "앞면 보기" : "뒷면 보기"}`}
        className="block w-full h-full p-0 border-0 bg-transparent cursor-pointer rounded-xl transition-transform duration-300 ease-out hover:scale-[1.03] origin-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
        style={{ perspective: "1000px" }}
      >
        <div
          className="relative w-full h-full transition-transform duration-500 ease-in-out"
          style={{
            transformStyle: "preserve-3d",
            transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          <div
            className="absolute inset-0 rounded-xl overflow-hidden"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(0deg)",
            }}
          >
            {front}
          </div>
          <div
            className={`absolute inset-0 rounded-xl overflow-hidden flex flex-col text-left ${
              isCompact ? "p-2.5 sm:p-3 justify-start" : "p-3 sm:p-4 justify-center"
            } ${
              isGoal
                ? "bg-primary-600 text-background-50 border border-primary-700"
                : "bg-background-50 text-foreground-900 border border-background-200"
            }`}
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            <p className={`text-[10px] sm:text-xs font-bold uppercase tracking-wide ${isGoal ? "text-background-50/80" : card.backAccent}`}>
              {card.backTitle}
            </p>
            <p className={`mt-1 text-[10px] sm:text-xs font-semibold leading-snug ${isGoal ? "text-background-50" : "text-foreground-950"}`}>
              {card.backBody}
            </p>
            <ul className={`mt-1.5 space-y-0.5 text-[9px] sm:text-[11px] leading-relaxed ${isGoal ? "text-background-50/90" : "text-foreground-700"}`}>
              {card.backItems.map((item) => (
                <li key={item} className="flex items-start gap-1">
                  <span className={`mt-1 w-1 h-1 rounded-full shrink-0 ${isGoal ? "bg-background-50/70" : "bg-primary-400"}`} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </button>
    </div>
  );
}

export default function Hero({ onApply, isManuallyClosed }: HeroProps) {
  const [activeCohortId, setActiveCohortId] = useState<number | null>(null);
  const [flippedCards, setFlippedCards] = useState<Set<HeroCardId>>(new Set());
  const { schedules: classSchedules } = useClassSchedules(activeCohortId);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("active_cohort_id");
      if (raw) setActiveCohortId(Number(raw));
    } catch {
      // storage unavailable
    }
  }, []);

  const startDates = classSchedules.map((s) => formatDateKorean(s.start_date));
  const startLabel = startDates.length > 0
    ? `${startDates.join(", ")} 개강`
    : "개강일 미정";

  const handleCardClick = (id: HeroCardId) => {
    setFlippedCards((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderCardFront = (id: HeroCardId) => {
    switch (id) {
      case "travel":
        return (
          <div className="relative w-full h-full">
            <img
              src="https://readdy.ai/api/search-image?query=East%20Asian%20young%20traveler%20asking%20for%20directions%20from%20friendly%20Japanese%20local%20on%20a%20beautiful%20traditional%20Japanese%20street%2C%20two%20people%20talking%20and%20gesturing%20warmly%2C%20warm%20soft%20natural%20daylight%2C%20muted%20earth%20tone%20colors%2C%20cream%20and%20warm%20beige%20palette%2C%20lifestyle%20travel%20photography%2C%20gentle%20shallow%20depth%20of%20field%2C%20traditional%20Japanese%20architecture%20in%20background%2C%20calm%20inviting%20mood%2C%20genuine%20interaction%20moment&width=640&height=480&seq=hero-travel-conv-1&orientation=landscape&nocache=true"
              alt="일본 거리에서 길 묻고 실제 회화를 나누는 모습"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            <div className="absolute bottom-3 left-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50/80 backdrop-blur text-[10px] font-semibold text-foreground-700">
                <i className="ri-chat-smile-2-line text-primary-500"></i>
                여행 회화
              </span>
            </div>
          </div>
        );
      case "goal":
        return (
          <div className="relative w-full h-full rounded-xl bg-primary-500 text-background-50 p-4 sm:p-5 flex items-center justify-start text-left">
            <div className="font-heading text-base sm:text-lg leading-tight opacity-90 pl-1 sm:pl-2">
              8주 후
              <br />
              여행에서
              <br />
              자신 있게
            </div>
            <div className="absolute bottom-4 right-4">
              <i className="ri-chat-smile-3-line text-xl sm:text-2xl"></i>
            </div>
          </div>
        );
      case "media":
        return (
          <div className="relative w-full h-full">
            <img
              src="https://readdy.ai/api/search-image?query=Person%20relaxing%20at%20home%20watching%20Japanese%20anime%20on%20laptop%20screen%20with%20warm%20table%20lamp%20light%2C%20cozy%20minimal%20room%2C%20soft%20cream%20beige%20color%20palette%2C%20gentle%20atmosphere%2C%20lifestyle%20photography%2C%20muted%20warm%20tones%2C%20calm%20and%20peaceful%20mood&width=480&height=320&seq=hero-media-watch-1&orientation=landscape"
              alt="일본 미디어 원문으로 즐기기"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            <div className="absolute bottom-3 left-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50/80 backdrop-blur text-[10px] font-semibold text-foreground-700">
                <i className="ri-tv-2-line text-accent-500"></i>
                미디어 감상
              </span>
            </div>
          </div>
        );
      case "daily":
        return (
          <div className="relative w-full h-full">
            <img
              src="https://readdy.ai/api/search-image?query=Overhead%20slightly%20angled%20top-down%20shot%20looking%20down%20at%20three%20young%20East%20Asian%20friends%20in%20their%20early%20twenties%20gathered%20around%20a%20small%20wooden%20cafe%20table%20with%20coffee%20cups%20and%20pastries%2C%20one%20Korean%20traveler%20and%20two%20Japanese%20locals%2C%20candid%20laughter%20and%20animated%20conversation%2C%20hands%20gesturing%20visible%20from%20above%2C%20warm%20pendant%20lamp%20creating%20cozy%20glow%20on%20the%20table%20surface%2C%20cream%20and%20warm%20beige%20interior%20with%20wooden%20textures%2C%20lifestyle%20editorial%20photography%20from%20above%2C%20genuine%20friendship%20and%20connection%2C%20soft%20muted%20earth%20tones%2C%20intimate%20cozy%20atmosphere%2C%20unique%20birds-eye%20perspective&width=480&height=320&seq=hero-cafe-conv-1&orientation=landscape&nocache=true"
              alt="일본 카페에서 20대 새로운 친구들과 편안하게 대화하는 모습"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            <div className="absolute bottom-3 left-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50/80 backdrop-blur text-[10px] font-semibold text-foreground-700">
                <i className="ri-cup-line text-primary-500"></i>
                일상 대화
              </span>
            </div>
          </div>
        );
    }
  };

  return (
    <section id="top" className="relative pt-24 md:pt-32 pb-12 md:pb-24 bg-background-50 overflow-hidden">
      <div className="absolute -top-16 -right-24 w-[420px] h-[420px] rounded-full bg-primary-100/60 blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-24 -left-24 w-[380px] h-[380px] rounded-full bg-accent-100/50 blur-3xl pointer-events-none"></div>

      <div className="relative max-w-6xl mx-auto px-4 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs md:text-sm font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
            8주 실전 온라인 클래스 · 최대 5명 소수정예
          </span>

          <h1 className="mt-6 font-heading font-black text-[20px] sm:text-4xl md:text-6xl leading-tight tracking-tight text-foreground-950">
            이해하고,
            <br />
            <span className="text-foreground-950">직접 말하는 </span>
            <span className="text-primary-600">일본어</span>
          </h1>

          <p className="mt-5 text-base md:text-xl font-semibold text-primary-600">
            왕초보 일본어 8주 실전 기초반 1기 모집!
          </p>

          <p className="mt-2 text-sm md:text-base font-medium text-accent-600">
            <i className="ri-calendar-check-line mr-1.5"></i>
            {startLabel}
          </p>

          {classSchedules.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground-600">
              {classSchedules.map((s) => (
                <span key={s.id} className="inline-flex items-center gap-1">
                  <span className="font-semibold text-foreground-700">{daysToLabel(s.days)}반</span>
                  <span>{s.start_time}~{s.end_time}</span>
                </span>
              ))}
            </div>
          )}

          <p className="mt-4 max-w-lg text-sm md:text-[17px] leading-relaxed text-foreground-700">
            일본 여행에서 필요한 기본 회화부터 애니메이션 · 드라마 · 음악 등 좋아하는 일본
            미디어를 원문으로 즐기기 위한 기초까지 함께 배울 수 있습니다.
          </p>
          <p className="mt-2 max-w-lg text-sm md:text-[17px] leading-relaxed text-foreground-700">
            8주 동안 내 입에서 나오는 일본어를 만들어 드릴게요.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-foreground-800">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                <i className="ri-team-line"></i>
              </span>
              <span className="break-all">최대 5명 소수정예</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                <i className="ri-calendar-2-line"></i>
              </span>
              <span className="break-all">주 2회 진행</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                <i className="ri-time-line"></i>
              </span>
              <span className="break-all">총 16회 (8주)</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                <i className="ri-computer-line"></i>
              </span>
              <span className="break-all">온라인 Zoom</span>
            </div>
          </div>

          <div className="mt-7 max-w-lg">
            <DeadlineBanner />
          </div>

          <div className="mt-6 flex flex-col gap-1">
            <span className="text-sm text-foreground-400 line-through ml-0.5">150,000원</span>
            <div className="flex items-baseline gap-1 -mt-0.5">
              <span className="text-sm font-medium text-foreground-600">월</span>
              <span className="font-heading text-4xl md:text-5xl font-bold text-foreground-950 tracking-tight">
                120,000
              </span>
              <span className="text-sm font-medium text-foreground-600">원</span>
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[11px] font-semibold">
                <i className="ri-sparkling-line text-xs"></i>
                1기 특별가
              </span>
            </div>
          </div>



          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={onApply}
              disabled={isManuallyClosed}
              className={`inline-flex items-center justify-center gap-2 px-5 py-3 sm:px-7 sm:py-4 rounded-md text-sm sm:text-base font-semibold transition-colors whitespace-nowrap ${
                isManuallyClosed
                  ? "bg-background-300 text-foreground-400 cursor-not-allowed"
                  : "bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer"
              }`}
            >
              {isManuallyClosed ? "신청 마감" : "수강 신청하기"}
              <i className="ri-arrow-right-line"></i>
            </button>
            <a
              href="#curriculum"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("curriculum")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 sm:px-7 sm:py-4 rounded-md border border-background-300 hover:border-primary-400 text-foreground-900 hover:text-primary-600 text-sm sm:text-base font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              커리큘럼 먼저 보기
            </a>
          </div>
        </div>

        <div className="relative">
          <div className="relative rounded-2xl bg-background-100 border border-background-200 p-3 sm:p-6 md:p-8">
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
              {HERO_CARDS.map((card) => (
                <HeroFlipCard
                  key={card.id}
                  card={card}
                  isFlipped={flippedCards.has(card.id)}
                  onToggle={() => handleCardClick(card.id)}
                  front={renderCardFront(card.id)}
                />
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-foreground-600">
              <span className="flex items-center gap-1">
                <i className="ri-flight-takeoff-line"></i> 카드를 클릭하면 상세 내용이 나와요
              </span>
              <span className="flex items-center gap-1 text-primary-600 font-semibold">
                8주 완성
                <i className="ri-star-fill"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}